import type { ComponentProps, ReactNode, RefObject } from 'react';
import {
	Dialog,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogClose,
} from '@worldcoo/ui/components/dialog';
import { Button } from '@worldcoo/ui/components/button';
import { Gradient } from '@worldcoo/ui/custom/gradient';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { IsScrolledToBottom } from '../../-components/is-scrolled-to-bottom';

export function TermsCheckboxField(props: {
	field: {
		CheckboxField: React.ComponentType<ComponentProps<'button'> & { label?: ReactNode; acceptIndeterminate?: boolean }>;
	};
	labelText: string;
	linkText: string;
	dialogTitle: string;
	acceptButtonText: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	labelRef: RefObject<HTMLDivElement | null>;
	dialogRef: RefObject<HTMLDivElement | null>;
	textRef: RefObject<HTMLTableElement | HTMLDivElement | null>;
	termsContent: ReactNode;
	onAccept: () => void;
	hideUnresetTailwind?: boolean;
}) {
	return (
		<props.field.CheckboxField
			className="gap-3"
			label={
				<div ref={props.labelRef} className="font-normal">
					{props.labelText}
					<Dialog open={props.open} onOpenChange={props.onOpenChange}>
						<DialogTrigger className="text-primary contents cursor-pointer">{props.linkText}</DialogTrigger>
						<DialogContent
							ref={props.dialogRef}
							aria-describedby={undefined}
							// No enter/exit scale on this one, unlike every other dialog. It is a scroll
							// container with a `sticky bottom-0` footer, and WebKit resolves the sticky offset
							// against the *transform-scaled* box: while the box sits at 95% it reads the content
							// as overflowing by that missing 5% and lifts the footer by it, then relaxes the lift
							// to zero as the scale settles. The accept button rides that lift down. Measured in
							// WebKit: 31.6px on a 393x852 iPhone, 27.8px on an SE, 20.4px on desktop, all 0px
							// once the scale is neutralised. The drift tracks dialog height rather than viewport,
							// and it happens whether or not the content really overflows, which is what gives the
							// mechanism away.
							//
							// Deliberately not scoped to mobile — desktop Safari drifts too — and not solvable
							// with `transform-origin`, which only decides where the box grows from, not what
							// WebKit measures. Chrome re-resolves sticky every frame and never showed this, so
							// this is spec-ambiguous ground no engine should be trusted to get right. The fade
							// carries the entrance on its own.
							//
							// `svh` rather than `dvh`: a fixed element is centred against the initial containing
							// block, sized to the *small* viewport, while `dvh` follows the browser chrome — which
							// moves the moment the dialog locks the page scroll, re-heighting the box after it has
							// opened and dragging the same edge with it.
							//
							// The `- 2rem` gives it a margin top and bottom to match the one the shared content
							// supplies left and right. On a phone this box is close enough to the whole screen
							// that without it it stops reading as an overlay: the dim behind it only says "modal"
							// while some of it shows, and tapping outside to dismiss needs an outside to tap.
							className="scrollbar data-open:zoom-in-100 data-closed:zoom-out-100 flex max-h-[calc(100svh-2rem)] flex-col gap-6 overflow-y-auto sm:max-w-xl"
						>
							<DialogHeader>
								<DialogTitle className="capitalize">{props.dialogTitle}</DialogTitle>
							</DialogHeader>

							<DivWithPrimaryColorVariable className={props.hideUnresetTailwind ? undefined : 'unreset-tailwind'}>
								{props.termsContent}
							</DivWithPrimaryColorVariable>

							<DivWithPrimaryColorVariable className="sticky bottom-0 pt-12">
								{/* The overhang is capped at the dialog's own bottom padding on purpose. An
								    absolutely positioned descendant still counts toward scrollable overflow, so at
								    `-bottom-6` the fade reached 8px past the padding and added 8px of scrollable
								    area that holds nothing. That is enough to leave the footer resting 8px high
								    once it un-sticks, which read as the button shifting the moment the donor
								    scrolled to the end and it became pressable. */}
								<Gradient className="absolute -bottom-4 -z-10 h-[180px]" />
								{/* `DialogFooter` is built as a full-bleed bar: its `-mb-4` cancels the dialog's own
								    bottom padding so a footer with a background can sit on the box edge. This one has
								    no background, so all that negative margin does is park the accept button on the
								    bottom edge instead of on the padding line every other element is aligned to.
								    Cancelling it here rather than in the shared component, which the dialogs that do
								    want the bar still rely on. `pb-0` because the footer's own `p-4` was the only
								    reason the button looked inset at all, and with the margin gone it would stack on
								    top of the dialog padding and sit twice as far in. */}
								<DialogFooter className="mb-0 pb-0">
									<DialogClose asChild>
										<IsScrolledToBottom dialogRef={props.dialogRef}>
											{(scrolledToBottom) => (
												<Button
													type="button"
													size="xl"
													className="mx-auto"
													style={{ transition: 'all 0.15s ease-in-out' }}
													disabled={!scrolledToBottom}
													onClick={props.onAccept}
												>
													{props.acceptButtonText}
												</Button>
											)}
										</IsScrolledToBottom>
									</DialogClose>
								</DialogFooter>
							</DivWithPrimaryColorVariable>
						</DialogContent>
					</Dialog>
				</div>
			}
		/>
	);
}
